import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_CATEGORIES = [
  "Governance",
  "Financial",
  "Asset Management",
  "Membership",
  "Operations",
  "Policy",
  "Appointment",
  "Authorization",
  "Approval",
  "Other",
];

interface ResolutionCategorySelectProps {
  value: string;
  onChange: (value: string) => void;
  unaId: string;
  isRequired?: boolean;
  helpText?: string;
}

export const ResolutionCategorySelect = ({ 
  value, 
  onChange, 
  unaId,
  isRequired = false,
  helpText
}: ResolutionCategorySelectProps) => {
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState("");
  const queryClient = useQueryClient();

  // Fetch custom categories for this UNA
  const { data: customCategories = [] } = useQuery({
    queryKey: ["customResolutionCategories", unaId],
    queryFn: async () => {
      if (!unaId) return [];
      
      const { data, error } = await supabase
        .from("custom_resolution_categories")
        .select("*")
        .eq("una_id", unaId)
        .order("category_name");

      if (error) throw error;
      return data || [];
    },
    enabled: !!unaId,
  });

  // Create custom category mutation
  const createCustomCategory = useMutation({
    mutationFn: async (categoryName: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("custom_resolution_categories")
        .insert({
          una_id: unaId,
          category_name: categoryName.trim(),
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          throw new Error("This category already exists");
        }
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["customResolutionCategories", unaId] });
      onChange(data.category_name);
      setShowCustomDialog(false);
      setCustomCategoryName("");
      toast.success(`"${data.category_name}" added successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create custom category");
    },
  });

  // Delete custom category mutation
  const deleteCustomCategory = useMutation({
    mutationFn: async (categoryId: string) => {
      const { error } = await supabase
        .from("custom_resolution_categories")
        .delete()
        .eq("id", categoryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customResolutionCategories", unaId] });
      toast.success("Category deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete category");
    },
  });

  const handleAddCustomCategory = () => {
    if (!customCategoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    createCustomCategory.mutate(customCategoryName);
  };

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === "__ADD_CUSTOM__") {
      setShowCustomDialog(true);
    } else {
      onChange(selectedValue);
    }
  };

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="resolution_category">
            Resolution Category {isRequired && <span className="text-destructive">*</span>}
          </Label>
          {customCategories.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowManageDialog(true)}
              className="h-auto p-1 text-xs hover:text-primary"
            >
              Manage Custom Categories
            </Button>
          )}
        </div>

        {helpText && (
          <p className="text-sm text-muted-foreground">{helpText}</p>
        )}

        <Select value={value} onValueChange={handleSelectChange}>
          <SelectTrigger id="resolution_category">
            <SelectValue placeholder="Select resolution category" />
          </SelectTrigger>
          <SelectContent>
            {DEFAULT_CATEGORIES.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}

            {customCategories.map((category) => (
              <SelectItem key={category.id} value={category.category_name}>
                {category.category_name}
              </SelectItem>
            ))}

            <SelectItem value="__ADD_CUSTOM__">
              <div className="flex items-center gap-2 text-primary">
                <Plus className="h-4 w-4" />
                Other (Add Custom Category)
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Add Custom Category Dialog */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Resolution Category</DialogTitle>
            <DialogDescription>
              Create a custom category for your UNA resolutions. This will be saved and available for future use.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="custom_category_name">Category Name</Label>
              <Input
                id="custom_category_name"
                placeholder="e.g., Legal Compliance, Tax Planning"
                value={customCategoryName}
                onChange={(e) => setCustomCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddCustomCategory();
                  }
                }}
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCustomDialog(false);
                setCustomCategoryName("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddCustomCategory}
              disabled={createCustomCategory.isPending || !customCategoryName.trim()}
            >
              {createCustomCategory.isPending ? "Adding..." : "Add Category"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Custom Categories Dialog */}
      <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Custom Resolution Categories</DialogTitle>
            <DialogDescription>
              Delete custom categories you no longer need. Default categories cannot be deleted.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-4">
            {customCategories.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No custom categories created yet
              </p>
            ) : (
              customCategories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <span className="font-medium">{category.category_name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm(`Delete "${category.category_name}"? This cannot be undone.`)) {
                        deleteCustomCategory.mutate(category.id);
                      }
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowManageDialog(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
